/**
 * HarmonySelect — a pixel-faithful replica of the .nb-dd custom dropdown
 * from the Harmony design system (notes-board.css / notes-board.js).
 *
 * Replaces MUI Select in the ProjectTasks filter toolbar so we don't have
 * to fight MUI's internal padding/icon structure.
 */
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HiCheck, HiChevronDown } from 'shared/HarmonyIcon'

export interface HarmonySelectOption {
    value: string
    label: string
    /** Informational row — shown in the list but not clickable and never marked as selected. */
    disabled?: boolean
}

interface HarmonySelectProps {
    value: string
    options: HarmonySelectOption[]
    placeholder?: string
    onChange: (value: string) => void
    width?: number
    id?: string
    disabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    // .nb-dd
    root: {
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'top',
    },
    // .nb-dd-trigger
    trigger: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        height: pxToRem(38),
        padding: `0 ${pxToRem(11)} 0 ${pxToRem(14)}`,
        font: 'inherit',
        fontSize: pxToRem(12.5),
        fontWeight: 500,
        color: '#373F4E',
        background: '#fff',
        border: `1px solid #BFC6D4`,
        borderRadius: pxToRem(8),
        cursor: 'pointer',
        transition: 'background 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
        outline: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { borderColor: '#97A1B4' },
        '&:focus-visible': {
            borderColor: '#4C92D9',
            boxShadow: '0 0 0 3px rgba(76,146,217,0.18)',
        },
    },
    triggerOpen: {
        borderColor: '#4C92D9',
        boxShadow: '0 0 0 3px rgba(76,146,217,0.18)',
    },
    // .nb-dd-value
    value: {
        flex: 1,
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    valuePlaceholder: {
        color: '#6B7280',
        fontWeight: 400,
    },
    // .nb-dd-chevron
    chevron: {
        width: pxToRem(16),
        height: pxToRem(16),
        flexShrink: 0,
        color: '#6B7280',
        transition: 'transform 160ms ease',
        display: 'flex',
        alignItems: 'center',
    },
    chevronOpen: {
        transform: 'rotate(180deg)',
    },
    // .nb-dd-menu
    menu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: pxToRem(6),
        minWidth: '100%',
        maxWidth: `min(${pxToRem(360)}, 90vw)`,
        background: '#fff',
        border: `1px solid #E2E8F0`,
        borderRadius: pxToRem(8),
        boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
        padding: pxToRem(5),
        zIndex: 60,
        maxHeight: pxToRem(280),
        overflowY: 'auto',
        animation: '$ddIn 130ms ease',
    },
    '@keyframes ddIn': {
        from: { opacity: 0, transform: 'translateY(-4px)' },
        to: { opacity: 1, transform: 'none' },
    },
    // .nb-dd-item
    item: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        padding: `${pxToRem(9)} ${pxToRem(10)}`,
        border: 0,
        background: 'none',
        borderRadius: pxToRem(7),
        font: 'inherit',
        fontSize: pxToRem(13),
        color: '#373F4E',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background 120ms ease, color 120ms ease',
        '&:hover': { background: '#EEF2F7' },
        '&[aria-disabled="true"]': { cursor: 'default', opacity: 0.65 },
    },
    // .nb-dd-item.is-selected
    itemSelected: {
        background: 'rgba(76,146,217,0.12)',
        color: '#2F6FB8',
        fontWeight: 600,
        '&:hover': { background: 'rgba(76,146,217,0.18)' },
    },
    // keyboard-focused item (ArrowUp / ArrowDown)
    itemActive: {
        background: '#EEF2F7',
    },
    itemActiveSelected: {
        background: 'rgba(76,146,217,0.18)',
    },
    // .nb-dd-item-check
    check: {
        width: pxToRem(15),
        height: pxToRem(15),
        flexShrink: 0,
        color: '#2F6FB8',
        opacity: 0,
        display: 'flex',
        alignItems: 'center',
    },
    checkVisible: {
        opacity: 1,
    },
    itemLabel: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}))

const HarmonySelect = ({
    value,
    options,
    placeholder = 'Select',
    onChange,
    width,
    id,
    disabled = false,
}: HarmonySelectProps) => {
    const classes = useStyles()
    const [open, setOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)
    const rootRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const selected = options.find((o) => o.value === value)

    const close = useCallback(() => setOpen(false), [])

    const openMenu = useCallback(() => {
        const idx = options.findIndex((o) => o.value === value)
        setActiveIndex(idx >= 0 ? idx : 0)
        setOpen(true)
    }, [options, value])

    // Close on outside click
    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                close()
            }
        }
        document.addEventListener('mousedown', onDown, true)
        return () => document.removeEventListener('mousedown', onDown, true)
    }, [open, close])

    // Close on Escape; ArrowUp/Down to navigate; Enter to select
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Tab') {
                close()
            } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIndex((i) => Math.min(i + 1, options.length - 1))
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter') {
                e.preventDefault()
                const active = activeIndex >= 0 ? options[activeIndex] : undefined
                if (active && !active.disabled) {
                    onChange(active.value)
                    close()
                }
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, close, activeIndex, options, onChange])

    // Scroll active item into view
    useEffect(() => {
        if (!open || activeIndex < 0 || !menuRef.current) return
        const item = menuRef.current.children[activeIndex] as HTMLElement | undefined
        item?.scrollIntoView({ block: 'nearest' })
    }, [open, activeIndex])

    return (
        <div
            ref={rootRef}
            className={classes.root}
            style={width ? { width: pxToRem(width) } : undefined}
        >
            <HoverTooltip text={selected?.label}>
                <button
                    id={id}
                    type="button"
                    className={`${classes.trigger}${open ? ` ${classes.triggerOpen}` : ''}`}
                    onClick={() => { if (!disabled) open ? close() : openMenu() }}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault()
                            if (!open && !disabled) openMenu()
                        }
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-disabled={disabled || undefined}
                    disabled={disabled}
                >
                    <span
                        className={`${classes.value}${
                            !selected ? ` ${classes.valuePlaceholder}` : ''
                        }`}
                    >
                        {selected ? selected.label : placeholder}
                    </span>
                    <span className={`${classes.chevron}${open ? ` ${classes.chevronOpen}` : ''}`}>
                        <HiChevronDown size={16} />
                    </span>
                </button>
            </HoverTooltip>

            {open && (
                <div ref={menuRef} className={classes.menu} role="listbox">
                    {options.map((o, idx) => {
                        const isSelected = !o.disabled && o.value === value
                        const isActive = !o.disabled && idx === activeIndex
                        let itemClass = classes.item
                        if (isSelected) itemClass += ` ${classes.itemSelected}`
                        if (isActive) {
                            itemClass += isSelected
                                ? ` ${classes.itemActiveSelected}`
                                : ` ${classes.itemActive}`
                        }
                        return (
                            <HoverTooltip key={`${o.value}-${idx}`} text={o.label} placements={['right']}>
                                <button
                                    type="button"
                                    role="option"
                                    tabIndex={-1}
                                    aria-selected={isSelected}
                                    aria-disabled={o.disabled}
                                    className={itemClass}
                                    onClick={() => {
                                        if (o.disabled) return
                                        onChange(o.value)
                                        close()
                                    }}
                                    onMouseEnter={() => { if (!o.disabled) setActiveIndex(idx) }}
                                >
                                    <span
                                        className={`${classes.check}${
                                            isSelected ? ` ${classes.checkVisible}` : ''
                                        }`}
                                    >
                                        <HiCheck size={15} />
                                    </span>
                                    <span className={classes.itemLabel}>{o.label}</span>
                                </button>
                            </HoverTooltip>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default HarmonySelect
